import type { SuggestedOperationalAction } from '../api';
import { actionLabel } from './operation/actionLabels';

/**
 * Where an incident's suggested action takes the operator (UX-01). Every
 * action opens a real workflow — the deployment, its log, its rollback
 * dialog, the environment's "Back up now" dialog, a backup's restore test,
 * the readiness check — and nothing runs until the operator confirms there.
 * An action whose target cannot be resolved has no destination and is not
 * shown: the dialog never reports an operation that did not happen.
 */
export interface IncidentActionTarget {
  section: 'deployments' | 'recovery' | 'infrastructure' | 'monitoring';
  /** The environment the target belongs to, when the action names one. */
  environmentId?: string;
  params: Record<string, string>;
  /** The button's words: what the click opens. */
  label: string;
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

export function incidentActionTarget(action: SuggestedOperationalAction): IncidentActionTarget | null {
  const payload = action.action_payload_json ?? {};
  const deploymentId = str(payload.deployment_id);
  const environmentId = str(payload.environment_id);
  switch (action.action_type) {
    case 'view_deployment':
      return deploymentId ? { section: 'deployments', environmentId, params: { deployment: deploymentId }, label: 'View deployment' } : null;
    case 'rollback_deployment':
      return deploymentId
        ? { section: 'deployments', environmentId, params: { deployment: deploymentId, intent: 'rollback' }, label: 'Review rollback' }
        : null;
    case 'view_logs':
      return deploymentId ? { section: 'deployments', environmentId, params: { deployment: deploymentId, intent: 'log' }, label: actionLabel.fullLogs } : null;
    case 'trigger_backup':
      return environmentId ? { section: 'recovery', environmentId, params: { intent: 'backup' }, label: 'Review backup' } : null;
    case 'test_restore': {
      const point = str(payload.recovery_point_id);
      return environmentId && point
        ? { section: 'recovery', environmentId, params: { intent: 'test_restore', point }, label: 'Review restore test' }
        : null;
    }
    case 'run_readiness_audit':
      return environmentId ? { section: 'infrastructure', environmentId, params: { intent: 'readiness' }, label: actionLabel.investigate } : null;
    default:
      return null;
  }
}
